<template>

    <Panel>

        <PanelHeader heading="Catalogue" subheading="Manage entries and access to the catalogue" />

        <PanelBody>
            
            <Fieldset>

                <FormField :field="state.sale_form.fields.catalogue_status" namespace="sale_" :name="'catalogue_status'" class="mb-4" />

                <FormField :field="state.sale_form.fields.entries_invited" namespace="sale_" :name="'entries_invited'" class="mb-4" />

                <Message :active="state.catalogue_warning.active" type="error" :heading="state.catalogue_warning.message" class="mb-8"/>

                <div class="min-w-0 flex-1 px-0 md:grid md:grid-cols-2 md:gap-6">
                    <div class="col-span-1" v-show="state.sale_form.fields.entries_invited_unit_before.active && state.sale_form.fields.entries_invited_number_before.active">
                        <legend class="block text-sm font-medium text-gray-700 mb-2">Entries open</legend>
                        <div class="min-w-0 flex-1 px-0 md:grid md:grid-cols-4 md:gap-2">
                            <div class="col-span-1">
                                <FormField :field="state.sale_form.fields.entries_invited_number_before" namespace="sale_" :name="'entries_invited_number_before'" class="mb-4" />
                            </div>
                            <div class="col-span-1">
                                <FormField :field="state.sale_form.fields.entries_invited_unit_before" namespace="sale_" :name="'entries_invited_unit_before'" class="mb-4" />
                            </div>
                            <div class="col-span-2">
                                <p class="mt-2 text-sm text-gray-500">Before the sale starts</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-span-1">
                        <legend class="block text-sm font-medium text-gray-700 mb-2">Entries close</legend>
                        <div class="min-w-0 flex-1 px-0 md:grid md:grid-cols-4 md:gap-2">
                            <div class="col-span-1">
                                <FormField :field="state.sale_form.fields.entries_close_number_before" namespace="sale_" :name="'entries_close_number_before'" class="mb-4" />
                            </div>
                            <div class="col-span-1">
                                <FormField :field="state.sale_form.fields.entries_close_unit_before" namespace="sale_" :name="'entries_close_unit_before'" class="mb-4" />
                            </div>
                            <div class="col-span-2">
                                <p class="mt-2 text-sm text-gray-500">Before the sale starts</p>
                            </div>
                        </div>
                    </div>
                    
                </div>

            </Fieldset>

        </PanelBody>

        <PanelFooter>
            <Button type="button" width="w-auto" class="ml-2" :onClick="() => next_step()" :disabled="!next_button_active">Next</Button>
        </PanelFooter>

    </Panel>

</template>

<script>
import { useSaleStore } from '@sto/sale.store'
import { useSystemStore } from '@sto/system.store'
import { useAuthStore } from '@sto/auth.store'
import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import { isValid, getPayload, getUserOrganisationType } from '@lib/helpers'
import { useRoute, useRouter } from 'vue-router'
import config from '@/config'
import LoadingIcon from '@ico/LoadingIcon.vue'
import moment from 'moment'
export default {
    components: {
        LoadingIcon
    },
    setup() {
        const ROUTE = useRoute()
        const ROUTER = useRouter()
        const SALE_STORE = useSaleStore()
        const SYSTEM_STORE = useSystemStore()
        const AUTH_STORE = useAuthStore()

        const sale = computed(() => SALE_STORE.sale)

        const init_entries_invited_number_before = sale.value?.entries_invited_number_before || (sale.value?.sale_type?.default_entries_invited_number_before || 7)
        const init_entries_close_number_before = sale.value?.entries_close_number_before || (sale.value?.sale_type?.default_entries_close_number_before || 1)

        const state = reactive({
            catalogue_warning: {
                active: false,
                message: ''
            },
            sale_form: {
                show_fields: false,
                valid: false,
                has_changes: computed(() => {
                    let has_changes = false
                    for (let f in state.sale_form.fields) {
                        if (state.sale_form.fields[f].value != state.sale_form.fields[f].stored_value) has_changes = true
                    }
                    return has_changes
                }),
                loading: false,
                fields: {
                    catalogue_status: {
                        active: true,
                        label: 'Catalogue status',
                        type: 'select',
                        value: sale.value?.catalogue_status || (sale.value?.sale_type?.default_catalogue_status || 'private'),
                        options: [
                            { value: 'private', label: 'Private' },
                            { value: 'none', label: 'None' },
                            { value: 'live', label: 'Live' },
                            { value: 'live_no_lot_nos', label: 'Live - No Lot Nos' },
                            { value: 'future', label: 'Future' }
                        ],
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    entries_invited: {
                        active: true,
                        label: 'Entries invited',
                        type: 'select',
                        value: sale.value?.entries_invited || (sale.value?.sale_type?.default_entries_invited || 'yes'),
                        options: [
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                        ],
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    entries_invited_unit_before: {
                        active: true,
                        label: '',
                        type: 'select',
                        value: sale.value?.entries_invited_unit_before || (sale.value?.sale_type?.default_entries_invited_unit_before || 'days'),
                        options: [
                            { value: 'days', label: init_entries_invited_number_before == 1 ? 'Day' : 'Days' },
                        ],
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    entries_close_unit_before: {
                        active: true,
                        label: '',
                        type: 'select',
                        value: sale.value?.entries_close_unit_before || (sale.value?.sale_type?.default_entries_close_unit_before || 'days'),
                        options: [
                            { value: '', label: 'Please choose...' },
                            { value: 'hours', label: init_entries_close_number_before == 1 ? 'Hour' : 'Hours' },
                            { value: 'days', label: init_entries_close_number_before == 1 ? 'Day' : 'Days' },
                        ],
                        stored_value: '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    entries_invited_number_before: {
                        active: true,
                        label: '',
                        type: 'number',
                        value: sale.value?.entries_invited_number_before || (sale.value?.sale_type?.default_entries_invited_number_before || 7),
                        stored_value: '',
                        min_value: 1,
                        max_value: 100,
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    entries_close_number_before: {
                        active: true,
                        label: '',
                        type: 'number',
                        value: sale.value?.entries_close_number_before || (sale.value?.sale_type?.default_entries_close_number_before || 1),
                        stored_value: '',
                        min_value: 0,
                        max_value: 100,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    }
                    //entries_invited_unit_before
                }
            },
        })

        /**
         * should the next button be available to the user?
         */
         const next_button_active = computed(() => {
            let active = true
            for (let f in state.sale_form.fields) {
                if (state.sale_form.fields[f].active && state.sale_form.fields[f].required && !state.sale_form.fields[f].value) active = false
            }
            if (state.catalogue_warning.active) active = false
            return active
        })

        const update_sale = async () => {

            if (state.sale_form.loading) return

            state.sale_form.loading = true

            if (!isValid(state.sale_form.fields)) {
                return state.sale_form.loading = false
            }

            const payload = getPayload(state.sale_form.fields)

            try {
                const sale = await SALE_STORE.updateSale(ROUTE.params.sale_id, payload)
                SALE_STORE.activateNextStep()

            } catch (error) {
                console.error('SALE_STORE.updateSale error', error)
                SYSTEM_STORE.addError('Something went wrong', error?.message || 'There was a problem updating the sale')
            } finally {
                state.sale_form.loading = false
            }
        }

        const check_entries_values = () => {
            // reset
            state.catalogue_warning.active = false
            state.catalogue_warning.message = ''
            // if either are inactive, proceed as if fine
            if (!state.sale_form.fields.entries_invited_number_before.active || !state.sale_form.fields.entries_close_number_before.value) return
            //if (state.sale_form.fields.entries_close_unit_before.value == state.sale_form.fields.entries_invited_unit_before.value)
            const entries_invited_proxy = moment().subtract(state.sale_form.fields.entries_invited_unit_before.value, state.sale_form.fields.entries_invited_number_before.value)
            const entries_close_proxy = moment().subtract(state.sale_form.fields.entries_close_unit_before.value, state.sale_form.fields.entries_close_number_before.value)
            // compare
            if (entries_invited_proxy.isSameOrAfter(entries_close_proxy)) {
                state.catalogue_warning.active = true
                state.catalogue_warning.message = 'Entries open must be before entries close!'
            }
        }

        const next_step = async () => {
            await update_sale()
        }

        watch(
            () => [
                state.sale_form.fields.entries_invited_number_before.value,
                state.sale_form.fields.entries_close_number_before.value
            ],
            () => {
                state.sale_form.fields.entries_close_unit_before.options = [
                    { value: 'hours', label: state.sale_form.fields.entries_close_number_before.value == 1 ? 'Hour' : 'Hours' },
                    { value: 'days', label: state.sale_form.fields.entries_close_number_before.value == 1 ? 'Day' : 'Days' },
                ]

                state.sale_form.fields.entries_invited_unit_before.options = [
                    { value: 'days', label: state.sale_form.fields.entries_invited_number_before.value == 1 ? 'Day' : 'Days' },
                ]

                check_entries_values()
            }
        )

        onBeforeMount(() => {
            check_entries_values()
        })

        return {
            SALE_STORE,
            ROUTER,
            next_button_active,
            state,
            next_step
        }
    }
}

</script>

